import React from "react";
import { Oval } from "react-loader-spinner";
import type { ScanResult, ScanFieldKey } from "../../../types/scan";
import { needsFieldVerification } from "../../../utils/scan/ocr";
import { getQrReadStatus, getQrValiditySummary, getLowConfidenceFields } from "../../../utils/scan/scanFeedback";

interface Props {
  /** Accumulated partial result (OCR fields after OCR step, qrResult after fetch). */
  partialResult?: Partial<ScanResult> | null;
  /** Full result when scan is done. */
  result?: ScanResult | null;
  className?: string;
}

function CheckIcon({ ok }: { ok: boolean }) {
  return ok ? (
    <span className="text-green-500 text-2xl" aria-hidden="true">✓</span>
  ) : (
    <span className="text-red-500 text-2xl" aria-hidden="true">✗</span>
  );
}

function PendingIcon() {
  return (
    <span className="text-amber-500 text-xl" aria-hidden="true">−</span>
  );
}

function WarnIcon() {
  return (
    <span className="text-amber-400 text-xl" aria-hidden="true">⚠</span>
  );
}

function LoadingIcon() {
  return (
    <Oval height={24} width={24} strokeWidth={4} color="#fff" secondaryColor="rgba(255,255,255,0.4)" aria-hidden />
  );
}

function OcrFieldRow({
  label,
  fieldKey,
  data,
  hasField,
}: {
  label: string;
  fieldKey: ScanFieldKey;
  data: Partial<ScanResult>;
  hasField: boolean;
}) {
  const value = data[fieldKey] as string | undefined;
  const filled = Boolean(value?.trim());
  const verify = needsFieldVerification(data.fieldConfidence?.[fieldKey], filled);

  if (!hasField) {
    return (
      <div className="flex items-center justify-between gap-3">
        <span className="text-white text-14px">{label}</span>
        <LoadingIcon />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-white text-14px">{label}</span>
        {verify ? <WarnIcon /> : <CheckIcon ok={filled} />}
      </div>
      {verify && filled && (
        <span className="text-amber-300 text-11px pl-0">À vérifier</span>
      )}
    </div>
  );
}

const ProcessingOverlay = ({ partialResult, result, className = "" }: Props) => {
  const data = result ?? partialResult ?? {};
  const isDone = result != null;
  const scanResult = (result ?? partialResult) as ScanResult | undefined;

  const hasNom = "lastname" in data;
  const hasPrenom = "firstname" in data;
  const hasEntreprise = "company" in data;
  const hasQr = "qrResult" in data && data.qrResult != null;

  const qrRead = scanResult ? getQrReadStatus(scanResult) === "read" : false;
  const validity = scanResult && qrRead ? getQrValiditySummary(scanResult) : null;
  const anyVerify =
    scanResult != null &&
    getLowConfidenceFields(scanResult).some(
      (key) => key === "lastname" || key === "firstname" || key === "company"
    );

  return (
    <div
      className={`flex flex-col items-center justify-center flex-grow w-full relative ${className}`}
      style={{ minHeight: "200px", background: "rgba(24, 43, 72, 0.98)", zIndex: 100001 }}
    >
      <p className="mb-4 text-white font-roboto text-16px text-center px-4 font-medium">
        {isDone ? "Scan terminé" : "Scan en cours"}
      </p>
      <div className="flex flex-col gap-3 text-left w-full max-w-xs">
        <OcrFieldRow label="Nom" fieldKey="lastname" data={data} hasField={hasNom} />
        <OcrFieldRow label="Prénom" fieldKey="firstname" data={data} hasField={hasPrenom} />
        <OcrFieldRow label="Entreprise" fieldKey="company" data={data} hasField={hasEntreprise} />

        <div className="flex flex-col gap-0.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-white text-14px">QR code</span>
            {hasQr ? (
              qrRead ? <CheckIcon ok={true} /> : <CheckIcon ok={false} />
            ) : (
              <LoadingIcon />
            )}
          </div>
          {hasQr && (
            <span className="text-white/70 text-11px">
              {qrRead ? "Détecté" : "Non détecté"}
            </span>
          )}
        </div>

        {hasQr && qrRead && validity && (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-white text-14px">Validité carte</span>
              {validity.status === "valid" ? (
                <CheckIcon ok={true} />
              ) : validity.status === "invalid" ? (
                <CheckIcon ok={false} />
              ) : (
                <PendingIcon />
              )}
            </div>
            {validity.status === "valid" && (
              <span className="text-white/70 text-11px">Valide</span>
            )}
            {validity.status === "invalid" && (
              <span className="text-white/70 text-11px">
                Invalide{validity.detail ? ` — ${validity.detail}` : ""}
              </span>
            )}
            {validity.status === "pending" && (
              <span className="text-white/70 text-11px">
                {validity.detail ?? validity.label}
              </span>
            )}
          </div>
        )}

        {anyVerify && (
          <p className="text-amber-200 text-11px mt-1 leading-snug">
            Certains champs ont une confiance faible — vérifiez avant de valider.
          </p>
        )}
      </div>
    </div>
  );
};

export default ProcessingOverlay;
